// frontend/src/screens/ForgotPassword.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { requestPasswordReset } from '../services/authApi';

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!email.trim()) { setMsg(t('forgotPassword.errors.enterEmail')); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setMsg(t('forgotPassword.errors.invalidEmail'));
      return;
    }

    try {
      setLoading(true);
      await requestPasswordReset(email.trim());
      setOk(true);
      setMsg(t('forgotPassword.success.message'));
    } catch (err) {
      setOk(false);
      setMsg(err?.message || t('forgotPassword.errors.recoveryFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="forgotBgHost" style={S.page}>
      <style>{`
        #forgotBgHost::before {
          content: "";
          position: fixed;
          inset: -20%;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 18%, rgba(177,74,255,0.24) 0%, transparent 25%),
            radial-gradient(circle at 84% 58%, rgba(123,31,205,0.22) 0%, transparent 31%),
            radial-gradient(circle at 50% 106%, rgba(218,165,32,0.18) 0%, transparent 30%);
          filter: blur(2px);
        }

        #forgotBgHost::after {
          content: "";
          position: fixed;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,226,138,0.88) 0 1px, transparent 1.45px),
            radial-gradient(circle, rgba(255,255,255,0.58) 0 1px, transparent 1.35px),
            linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0.72));
          background-size: 112px 112px, 174px 174px, 100% 100%;
          background-position: 22px 18px, 86px 44px, center;
          opacity: 0.58;
          animation: forgot-star-drift 18s linear infinite;
        }

        @keyframes forgot-star-drift {
          from { background-position: 22px 18px, 86px 44px, center; }
          to { background-position: 22px 130px, 86px 218px, center; }
        }

        @keyframes forgot-card-wheel-spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          #forgotBgHost::after,
          #forgotCard::before {
            animation: none !important;
          }
        }

        #forgotCard {
          overflow: hidden;
        }

        #forgotCard::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: min(122vw, 540px);
          height: min(122vw, 540px);
          z-index: 0;
          pointer-events: none;
          border-radius: 50%;
          overflow: hidden;
          background-image: url("/logo/Zodiacus.jpeg");
          background-repeat: no-repeat;
          background-position: center center;
          background-size: cover;
          opacity: 0.36;
          mix-blend-mode: screen;
          filter: brightness(1.78) contrast(1.08) saturate(1.2) drop-shadow(0 0 30px rgba(218,165,32,0.34)) drop-shadow(0 0 46px rgba(154,47,255,0.26));
          animation: forgot-card-wheel-spin 155s linear infinite;
          transform: translate(-50%, -50%) rotate(0deg);
          transform-origin: center center;
        }

        #forgotCard > * {
          position: relative;
          z-index: 2;
        }

        #forgotCard input::placeholder {
          color: rgba(255,232,168,0.36);
        }

        @media (max-width: 540px) {
          #forgotContent {
            min-height: 100svh !important;
            padding: 116px 20px 30px !important;
            justify-content: center !important;
            animation: none !important;
            transform: translateY(46px) !important;
          }

          #forgotBackCircle {
            top: calc(-4px + env(safe-area-inset-top, 0px)) !important;
            width: 50px !important;
            height: 50px !important;
            font-size: 27px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0 !important;
            line-height: 1 !important;
          }

          #forgotLangTop {
            top: calc(-4px + env(safe-area-inset-top, 0px)) !important;
            right: calc(10px + env(safe-area-inset-right, 0px)) !important;
            padding: 0.48rem 0.72rem !important;
            gap: 6px !important;
            transform: scale(0.92);
            transform-origin: top right;
          }

          #forgotCard {
            padding: 26px 20px 24px !important;
            border-radius: 24px !important;
          }

          #forgotCard::before {
            width: min(116vw, 420px);
            height: min(116vw, 420px);
            opacity: 0.38;
          }

          #forgotCard [style*="grid-template-columns"] {
            grid-template-columns: 22px minmax(0, 1fr) !important;
            gap: 8px !important;
          }

          #forgotCard h1 {
            font-size: clamp(1.62rem, 7.2vw, 2.2rem) !important;
            line-height: 1.06 !important;
          }
        }
      `}</style>

      <button
        id="forgotBackCircle"
        type="button"
        aria-label={t('forgotPassword.back')}
        title={t('forgotPassword.back')}
        onClick={() => {
          const canGoBack = typeof window !== 'undefined' && window.history?.state && window.history.state.idx > 0;
          if (canGoBack) navigate(-1);
          else navigate('/login');
        }}
        style={S.backCircle}
      >
        <span aria-hidden="true" style={S.backArrow}>←</span>
      </button>

      <div id="forgotLangTop" style={S.langTop} aria-label={t('welcome.languageSelector')}>
        <span style={S.langIcon}>◎</span>
        <button
          type="button"
          onClick={() => i18n.changeLanguage('es')}
          style={{ ...S.langTopButton, opacity: i18n.language?.startsWith('es') ? 1 : 0.55 }}
        >
          ES
        </button>
        <span style={S.langDivider}>/</span>
        <button
          type="button"
          onClick={() => i18n.changeLanguage('en')}
          style={{ ...S.langTopButton, opacity: i18n.language?.startsWith('en') ? 1 : 0.55 }}
        >
          EN
        </button>
      </div>

      <main id="forgotContent" style={S.content}>
        <section id="forgotCard" style={S.card}>
          <div style={S.header}>
            <h1 style={S.title}>{t('forgotPassword.title')}</h1>
            <p style={S.subtitle}>
              {t(
                'forgotPassword.subtitle',
                i18n.language?.startsWith('en')
                  ? 'Enter your email and we will send you a recovery link.'
                  : 'Ingresá tu email y te enviaremos un enlace de recuperación.'
              )}
            </p>
          </div>

          {msg && (
            <div style={{ ...S.alert, ...(ok ? S.alertOk : S.alertError) }} role={ok ? 'status' : 'alert'} aria-live={ok ? 'polite' : 'assertive'}>
              {msg}
            </div>
          )}

          <form onSubmit={onSubmit} style={S.form} autoComplete="on">
            <div style={S.fieldRow}>
              <span aria-hidden="true" style={S.fieldIcon}>✉</span>
              <div style={S.fieldContent}>
                <label htmlFor="forgotEmail" style={S.label}>{t('forgotPassword.emailLabel')}</label>
                <input
                  id="forgotEmail"
                  name="email"
                  type="email"
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={S.input}
                  autoComplete="email"
                  inputMode="email"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              style={{ ...S.submit, opacity: loading ? 0.66 : 1 }}
              disabled={loading || !email.trim()}
            >
              <span aria-hidden="true" style={S.btnSpark}>✦</span>
              {loading ? t('forgotPassword.sending') : t('forgotPassword.submit')}
              <span aria-hidden="true" style={S.btnSpark}>✦</span>
            </button>
          </form>
        </section>

        <div style={S.loginBox}>
          <div style={S.dividerRow}>
            <span style={S.dividerLine} />
            <span style={S.dividerText}>
              {t(
                'forgotPassword.remembered',
                i18n.language?.startsWith('en')
                  ? 'Sign in to continue'
                  : 'Iniciá sesión para continuar'
              )}
            </span>
            <span style={S.dividerLine} />
          </div>
          <Link to="/login" style={S.loginButton}>{t('forgotPassword.login')}</Link>
        </div>

        <div aria-hidden="true" style={S.bottomSigil}>
          <span />
          <i>✦</i>
          <span />
        </div>
      </main>
    </div>
  );
};

const S = {
  page: {
    minHeight: '100vh',
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    color: '#fff',
    background:
      'radial-gradient(circle at 50% 30%, rgba(110,24,184,0.32) 0%, rgba(30,0,70,0.48) 32%, rgba(4,1,15,0.98) 74%), linear-gradient(180deg, #05010F 0%, #150028 50%, #04000D 100%)',
  },
  backCircle: {
    position: 'fixed',
    top: 'calc(-2px + env(safe-area-inset-top, 0px))',
    left: 'calc(22px + env(safe-area-inset-left, 0px))',
    zIndex: 250,
    width: 54,
    height: 54,
    borderRadius: '50%',
    border: '1px solid rgba(255,218,116,0.78)',
    background: 'rgba(8,2,24,0.54)',
    color: '#FFD979',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
    boxShadow: '0 0 24px rgba(218,165,32,0.22), inset 0 0 18px rgba(218,165,32,0.05)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  backArrow: {
    display: 'block',
    lineHeight: 1,
    transform: 'translateY(-1px)',
  },
  langTop: {
    position: 'fixed',
    top: 'calc(-2px + env(safe-area-inset-top, 0px))',
    right: 'calc(22px + env(safe-area-inset-right, 0px))',
    zIndex: 250,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '0.62rem 0.9rem',
    borderRadius: '999px',
    border: '1px solid rgba(232,95,255,0.72)',
    background: 'rgba(14,4,31,0.72)',
    color: '#FFF0B8',
    boxShadow: '0 0 22px rgba(190,60,255,0.34), inset 0 0 18px rgba(218,165,32,0.08)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
  langIcon: {
    fontSize: '1.1rem',
    color: '#FFE8A8',
    lineHeight: 1,
  },
  langDivider: {
    color: 'rgba(255,232,168,0.45)',
    fontWeight: 800,
  },
  langTopButton: {
    border: 0,
    background: 'transparent',
    color: '#FFF0B8',
    fontWeight: 900,
    letterSpacing: '0.06em',
    fontSize: '0.95rem',
    cursor: 'pointer',
    padding: 0,
  },
  content: {
    position: 'relative',
    zIndex: 5,
    minHeight: '100svh',
    width: '100%',
    maxWidth: 760,
    margin: '0 auto',
    padding: '116px 26px 38px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    position: 'relative',
    zIndex: 4,
    width: '100%',
    maxWidth: 520,
    borderRadius: 32,
    border: '1px solid rgba(232,95,255,0.44)',
    background: 'linear-gradient(180deg, rgba(20,5,42,0.26), rgba(8,2,24,0.32))',
    boxShadow: '0 24px 70px rgba(0,0,0,0.48), 0 0 32px rgba(171,54,255,0.20), inset 0 1px 0 rgba(255,255,255,0.06)',
    color: '#FFF6D8',
    padding: '32px 34px 30px',
    boxSizing: 'border-box',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
  },
  header: {
    textAlign: 'center',
    marginBottom: 24,
  },
  title: {
    color: '#FFD979',
    margin: 0,
    fontWeight: 500,
    fontSize: 'clamp(1.72rem, 5.4vw, 2.45rem)',
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
    textShadow: '0 0 18px rgba(218,165,32,0.18)',
    lineHeight: 1.08,
  },
  subtitle: {
    margin: '10px 0 0',
    color: 'rgba(255,255,255,0.74)',
    fontSize: 'clamp(0.96rem, 3vw, 1.1rem)',
    lineHeight: 1.35,
  },
  alert: {
    padding: '12px 15px',
    borderRadius: 14,
    textAlign: 'center',
    marginBottom: 18,
    border: '1px solid rgba(255,255,255,0.14)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
    fontWeight: 800,
  },
  alertOk: {
    background: 'linear-gradient(180deg, rgba(31,140,80,0.92), rgba(14,90,54,0.92))',
    color: 'white',
  },
  alertError: {
    background: 'linear-gradient(180deg, rgba(160,20,48,0.9), rgba(102,6,32,0.9))',
    color: 'white',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    marginTop: 0,
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '26px minmax(0, 1fr)',
    gap: 10,
    alignItems: 'end',
    marginTop: 14,
  },
  fieldIcon: {
    color: '#FFD979',
    fontSize: 24,
    lineHeight: '52px',
    textAlign: 'center',
    textShadow: '0 0 12px rgba(218,165,32,0.35)',
  },
  fieldContent: {
    minWidth: 0,
  },
  label: {
    display: 'block',
    marginBottom: 8,
    color: 'rgba(255,255,255,0.92)',
    fontWeight: 700,
    fontSize: '0.95rem',
  },
  input: {
    width: '100%',
    height: 54,
    padding: '0 16px',
    borderRadius: 14,
    border: '1px solid rgba(209,120,255,0.56)',
    fontSize: 16,
    outline: 'none',
    color: '#FFF6D8',
    background: 'rgba(5,1,18,0.52)',
    boxSizing: 'border-box',
    boxShadow: 'inset 0 0 18px rgba(0,0,0,0.18)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  },
  submit: {
    width: '100%',
    minHeight: 62,
    marginTop: 28,
    borderRadius: '999px',
    border: '1px solid rgba(255,230,147,0.95)',
    background: 'linear-gradient(180deg, #FFE08B 0%, #D9902E 48%, #9B551B 100%)',
    color: '#1E1230',
    fontWeight: 900,
    fontSize: 'clamp(1rem, 3.5vw, 1.18rem)',
    cursor: 'pointer',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    boxShadow: '0 0 28px rgba(218,165,32,0.48), 0 0 22px rgba(205,69,255,0.42), inset 0 1px 0 rgba(255,255,255,0.55)',
    transition: 'transform 140ms ease, box-shadow 140ms ease, filter 140ms ease',
    WebkitTapHighlightColor: 'transparent',
  },
  btnSpark: {
    fontSize: '1.05rem',
    color: 'rgba(255,255,255,0.9)',
    textShadow: '0 0 10px rgba(255,255,255,0.8)',
  },
  loginBox: {
    width: '100%',
    maxWidth: 520,
    marginTop: 18,
  },
  dividerRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    color: 'rgba(255,255,255,0.74)',
    marginBottom: 18,
  },
  dividerLine: {
    height: 1,
    flex: 1,
    background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.42), transparent)',
  },
  dividerText: {
    whiteSpace: 'nowrap',
    fontSize: '1rem',
  },
  loginButton: {
    width: 'min(100%, 510px)',
    minHeight: 58,
    margin: '0 auto',
    borderRadius: '999px',
    border: '1px solid rgba(232,95,255,0.82)',
    background: 'rgba(12,2,30,0.42)',
    color: '#FFE8A8',
    boxShadow: '0 0 24px rgba(196,69,255,0.36), inset 0 0 18px rgba(218,165,32,0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    fontSize: 'clamp(0.95rem, 3.4vw, 1.1rem)',
    fontWeight: 900,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  bottomSigil: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    color: '#FFE09A',
    opacity: 0.78,
    marginTop: 24,
    filter: 'drop-shadow(0 0 12px rgba(218,165,32,0.45))',
  },
};

export default ForgotPassword;